import { Injectable } from '@angular/core';
import { UnitService } from '../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { AnessaTranslationService } from '../../helper/translation/anessa-translation.service';
import { ConfiguredToastrService } from '../../helper/toastr/configured-toastr.service';
import { ValidationSectionName } from '../../models/data-structures/project-validation';
import { InputSummary, InputSummaryItem } from '../../models/input-summary';
import { DigesterPeriodType, DigesterType, PlantSpecificationsPropName, PlantUnitType } from '../../models/project/plant-specifications/plant-specifications.model';
import { Feedstock, FeedstockPropName } from '../../models/project/feedstock/feedstock.model';
import { FeedstockBmpType } from '../../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential.model';
import { FeedstockBmpPotentialDirectPropName } from '../../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-potential-direct.model';
import { BmpYieldType, FeedstockBmpPotentialYieldPropName } from '../../models/project/feedstock/feedstock-bmp-potential/feedstock-bmp-potential-type-models/feedstock-bmp-potential-yield.model';
import { FinancialPropName } from '../../models/project/financial/financial.model';
import { FinancialInvestment, FinancialInvestmentPropName } from '../../models/project/financial/financial-investment/financial-investment.model';
import { PlantSpecifications } from '../../models/project/plant-specifications/plant-specifications.model';
import { Project } from '../../models/project/project.model';
import Utils from '../../util';

@Injectable({
  providedIn: 'root'
})
export class DefaultAssumptionsService {

  projectAssumptions = {};

  constructor(
    private unitService: UnitService,
    private toastrService: ConfiguredToastrService,
    private translationService: AnessaTranslationService,
  ) {
    // Initialize project asumptions
    this.projectAssumptions = {};
    this.projectAssumptions[ValidationSectionName.Feedstock] = new InputSummary();
    this.projectAssumptions[ValidationSectionName.PlantSpecifications] = new InputSummary();
    this.projectAssumptions[ValidationSectionName.Financial] = new InputSummary();
  }

  getProjectAssumptions() {
    return this.projectAssumptions;
  }

  setProjectAssumptions(project: Project, overwriteValues: boolean = false) {
    // Reset assumptions
    this.projectAssumptions[ValidationSectionName.Feedstock] = new InputSummary();
    this.projectAssumptions[ValidationSectionName.PlantSpecifications] = new InputSummary();
    this.projectAssumptions[ValidationSectionName.Financial] = new InputSummary();

    for (const feedstock of project.feedstocks) {
      const index = project.feedstocks.indexOf(feedstock);
      this.setFeedstockAssumptions(feedstock, index, feedstock.bmpPotential.type, feedstock.bmpPotential.bmpYield.yieldType);
    }
    this.setDigesterAssumptions(project.plantSpecifications, project.feedstocks, overwriteValues);
    this.setPlantUnitsAssumptions(project.plantSpecifications, overwriteValues);
    this.setInvestmentAssumptions(project.financial.investment);
  }

  /**
   * Calculates the weighted average TS percent of the feedstocks
   * @param feedstocks list of feedstocks
   */
  private getWeightedAverageFeedstockTS(feedstocks: Feedstock[]) {
    if (feedstocks.length === 0) {
      return 0;
    }

    /**
     * Keep track of whether any feedstocks were used in calculating the
     * weighted average. If non were used then return -1 so that the calling
     * function knows this value is useless.
     */
    let feedstockUsed = false;
    let totalMassKgDay = 0;
    const feedstockData: { ts: number, mass: number }[] = [];
    for (const feedstock of feedstocks) {
      const feedMass = feedstock.quantities.getMass();
      const feedPeriod = feedstock.quantities.period;
      const feedTs = feedstock.characteristics.ts;

      if (
        feedTs.value != null && feedTs.unit
        && feedMass.value != null && feedMass.unit
        && feedPeriod.value != null && feedPeriod.unit) {
        feedstockUsed = true;

        const tsUnit = this.unitService.getUnitById(feedstock.characteristics.ts.unit);
        const massUnit = this.unitService.getUnitById(feedstock.quantities.getMass().unit);
        const periodUnit = this.unitService.getUnitById(feedstock.quantities.period.unit);

        const tsPercent = tsUnit.convertToBase(feedTs.value);
        const massKg = massUnit.convertToBase(feedMass.value);
        const periodDay = periodUnit.convertToBase(feedPeriod.value);

        totalMassKgDay += massKg / periodDay;
        feedstockData.push({ ts: tsPercent, mass: massKg / periodDay });
      }
    }

    let averageTs = 0;
    feedstockData.forEach(item => {
      averageTs += item.ts * item.mass / totalMassKgDay;
    });
    if (feedstockUsed) {
      return averageTs * 100;
    }
    return -1;
  }

  /**
   * Checks whether the average TS of the feedstocks is larger than the digester
   * target TS
   * @param plantSpec reference to the plant specification
   * @param feedstockTs weighted average for feedstock TS
   */
  private checkTargetTs(plantSpec: PlantSpecifications, feedstockTs: number) {
    if (plantSpec.targetTS.value > feedstockTs) {
      this.toastrService.warning(this.translationService.get(
        'PROJECT.INITIAL.TTS_WARNING',
        {
          targetTs: plantSpec.targetTS.value,
          feedstockTs: Utils.roundToPrecision(feedstockTs, 2),
          digType: this.translationService.get(plantSpec.selectedDigester),
        },
      ), null, 0, true);
    }
  }

  /**
   * Sets the assumed values for the plant specification based on the digester type
   * @param plantSpec reference to the plant specification
   * @param feedstocks list of feedstocks
   */
  setDigesterAssumptions(plantSpec: PlantSpecifications, feedstocks: Feedstock[], overwriteValues: boolean = false) {
    const summary = this.projectAssumptions[ValidationSectionName.PlantSpecifications].subSection[PlantSpecificationsPropName.selectedDigester] = new InputSummary();

    const weightedFeedstockTS = this.getWeightedAverageFeedstockTS(feedstocks);

    const overheadVolume = new InputSummaryItem();
    const targetTS = new InputSummaryItem();
    const periodHRT = new InputSummaryItem();
    // const periodOLR = new InputSummaryItem();
    const workingDays = new InputSummaryItem();

    switch (plantSpec.selectedDigester) {
      case DigesterType.WET_MIXED:
        // 5%
        if (overwriteValues || plantSpec.overheadVolume.value == null) {
          plantSpec.overheadVolume.value = 5;
          plantSpec.overheadVolume.unit = '000000000000000000001801';
        }
        overheadVolume.name = PlantSpecificationsPropName.overheadVolume;
        overheadVolume.unit = plantSpec.overheadVolume.unit;
        overheadVolume.value = String(plantSpec.overheadVolume.value);
        overheadVolume.path = ['plantSpecifications', 'overheadVolume', 'value'];
        summary.inputs.push(overheadVolume);
        // 6%
        if (overwriteValues || plantSpec.targetTS.value == null) {
          plantSpec.targetTS.value = 6;
          plantSpec.targetTS.unit = '000000000000000000000101';
        }
        targetTS.name = PlantSpecificationsPropName.targetTS;
        targetTS.unit = plantSpec.targetTS.unit;
        targetTS.value = String(plantSpec.targetTS.value);
        targetTS.path = ['plantSpecifications', 'targetTS', 'value'];
        summary.inputs.push(targetTS);
        if (weightedFeedstockTS >= 0) {
          this.checkTargetTs(plantSpec, weightedFeedstockTS);
        }
        // 3 kg VS/m3/day
        // if (overwriteValues || plantSpec.periodOLR.value == null) {
        //   plantSpec.periodOLR.value = 3;
        //   plantSpec.periodOLR.unit = '000000000000000000003601';
        // }
        // periodOLR.name = PlantSpecificationsPropName.periodOLR;
        // periodOLR.unit = plantSpec.periodOLR.unit;
        // periodOLR.value = String(plantSpec.periodOLR.value);
        // periodOLR.path = ['plantSpecifications', 'periodOLR', 'value'];
        // summary.inputs.push(periodOLR);
        // 30 days
        if (overwriteValues || plantSpec.periodHRT.value == null) {
          plantSpec.periodHRT.value = 30;
          plantSpec.periodHRT.unit = '000000000000000000001302';
        }
        periodHRT.name = PlantSpecificationsPropName.periodHRT;
        periodHRT.unit = plantSpec.periodHRT.unit;
        periodHRT.value = String(plantSpec.periodHRT.value);
        periodHRT.path = ['plantSpecifications', 'periodHRT', 'value'];
        summary.inputs.push(periodHRT);
        plantSpec.periodType = DigesterPeriodType.HRT;
        break;
      case DigesterType.DRY_CONT:
        // 5%
        if (overwriteValues || plantSpec.overheadVolume.value == null) {
          plantSpec.overheadVolume.value = 5;
          plantSpec.overheadVolume.unit = '000000000000000000001801';
        }
        overheadVolume.name = PlantSpecificationsPropName.overheadVolume;
        overheadVolume.unit = plantSpec.overheadVolume.unit;
        overheadVolume.value = String(plantSpec.overheadVolume.value);
        overheadVolume.path = ['plantSpecifications', 'overheadVolume', 'value'];
        summary.inputs.push(overheadVolume);
        // 25%
        if (overwriteValues || plantSpec.targetTS.value == null) {
          plantSpec.targetTS.value = 25;
          plantSpec.targetTS.unit = '000000000000000000000101';
        }
        targetTS.name = PlantSpecificationsPropName.targetTS;
        targetTS.unit = plantSpec.targetTS.unit;
        targetTS.value = String(plantSpec.targetTS.value);
        targetTS.path = ['plantSpecifications', 'targetTS', 'value'];
        summary.inputs.push(targetTS);
        if (weightedFeedstockTS >= 0) {
          this.checkTargetTs(plantSpec, weightedFeedstockTS);
        }
        // 8 kg VS/m3/day
        // if (overwriteValues || plantSpec.periodOLR.value == null) {
        //   plantSpec.periodOLR.value = 8;
        //   plantSpec.periodOLR.unit = '000000000000000000003601';
        // }
        // periodOLR.name = PlantSpecificationsPropName.periodOLR;
        // periodOLR.unit = plantSpec.periodOLR.unit;
        // periodOLR.value = String(plantSpec.periodOLR.value);
        // periodOLR.path = ['plantSpecifications', 'periodOLR', 'value'];
        // summary.inputs.push(periodOLR);
        // 30 days
        if (overwriteValues || plantSpec.periodHRT.value == null) {
          plantSpec.periodHRT.value = 30;
          plantSpec.periodHRT.unit = '000000000000000000001302';
        }
        periodHRT.name = PlantSpecificationsPropName.periodHRT;
        periodHRT.unit = plantSpec.periodHRT.unit;
        periodHRT.value = String(plantSpec.periodHRT.value);
        periodHRT.path = ['plantSpecifications', 'periodHRT', 'value'];
        summary.inputs.push(periodHRT);
        plantSpec.periodType = DigesterPeriodType.HRT;
        break;
      case DigesterType.DRY_BATCH:
        // 5%
        if (overwriteValues || plantSpec.overheadVolume.value == null) {
          plantSpec.overheadVolume.value = 5;
          plantSpec.overheadVolume.unit = '000000000000000000001801';
        }
        overheadVolume.name = PlantSpecificationsPropName.overheadVolume;
        overheadVolume.unit = plantSpec.overheadVolume.unit;
        overheadVolume.value = String(plantSpec.overheadVolume.value);
        overheadVolume.path = ['plantSpecifications', 'overheadVolume', 'value'];
        summary.inputs.push(overheadVolume);
        // 350 days/year
        if (overwriteValues || plantSpec.workingDays.value == null) {
          plantSpec.workingDays.value = 350;
          plantSpec.workingDays.unit = '000000000000000000001302';
        }
        workingDays.name = PlantSpecificationsPropName.workingDays;
        workingDays.unit = plantSpec.workingDays.unit;
        workingDays.value = String(plantSpec.workingDays.value);
        workingDays.path = ['plantSpecifications', 'workingDays', 'value'];
        summary.inputs.push(workingDays);
        // 21 days
        if (overwriteValues || plantSpec.periodHRT.value == null) {
          plantSpec.periodHRT.value = 21;
          plantSpec.periodHRT.unit = '000000000000000000001302';
        }
        periodHRT.name = PlantSpecificationsPropName.periodHRT;
        periodHRT.unit = plantSpec.periodHRT.unit;
        periodHRT.value = String(plantSpec.periodHRT.value);
        periodHRT.path = ['plantSpecifications', 'periodHRT', 'value'];
        summary.inputs.push(periodHRT);
        plantSpec.periodType = DigesterPeriodType.HRT;
        break;
      case DigesterType.WET_LAGOON:
        // 5%
        if (overwriteValues || plantSpec.overheadVolume.value == null) {
          plantSpec.overheadVolume.value = 5;
          plantSpec.overheadVolume.unit = '000000000000000000001801';
        }
        overheadVolume.name = PlantSpecificationsPropName.overheadVolume;
        overheadVolume.unit = plantSpec.overheadVolume.unit;
        overheadVolume.value = String(plantSpec.overheadVolume.value);
        overheadVolume.path = ['plantSpecifications', 'overheadVolume', 'value'];
        summary.inputs.push(overheadVolume);
        // 3%
        if (overwriteValues || plantSpec.targetTS.value == null) {
          plantSpec.targetTS.value = 3;
          plantSpec.targetTS.unit = '000000000000000000000101';
        }
        targetTS.name = PlantSpecificationsPropName.targetTS;
        targetTS.unit = plantSpec.targetTS.unit;
        targetTS.value = String(plantSpec.targetTS.value);
        targetTS.path = ['plantSpecifications', 'targetTS', 'value'];
        summary.inputs.push(targetTS);
        if (weightedFeedstockTS >= 0) {
          this.checkTargetTs(plantSpec, weightedFeedstockTS);
        }
        // 2 kg VS/m3/day
        // if (overwriteValues || plantSpec.periodOLR.value == null) {
        //   plantSpec.periodOLR.value = 2;
        //   plantSpec.periodOLR.unit = '000000000000000000003601';
        // }
        // periodOLR.name = PlantSpecificationsPropName.periodOLR;
        // periodOLR.unit = plantSpec.periodOLR.unit;
        // periodOLR.value = String(plantSpec.periodOLR.value);
        // periodOLR.path = ['plantSpecifications', 'periodOLR', 'value'];
        // summary.inputs.push(periodOLR);
        // 60 days
        if (overwriteValues || plantSpec.periodHRT.value == null) {
          plantSpec.periodHRT.value = 60;
          plantSpec.periodHRT.unit = '000000000000000000001302';
        }
        periodHRT.name = PlantSpecificationsPropName.periodHRT;
        periodHRT.unit = plantSpec.periodHRT.unit;
        periodHRT.value = String(plantSpec.periodHRT.value);
        periodHRT.path = ['plantSpecifications', 'periodHRT', 'value'];
        summary.inputs.push(periodHRT);
        plantSpec.periodType = DigesterPeriodType.HRT;
        break;
    }
  }

  /**
   * Sets the assumed values for the plant specification based on the plant units
   * @param plantSpec reference to the plant specification
   */
  setPlantUnitsAssumptions(plantSpec: PlantSpecifications, overwriteValues: boolean = false) {
    const summary = this.projectAssumptions[ValidationSectionName.PlantSpecifications].subSection[PlantSpecificationsPropName.selectedUnits] = new InputSummary();

    for (const unit of plantSpec.selectedUnits) {
      const electricalEfficiency = new InputSummaryItem();
      const thermalEfficiency = new InputSummaryItem();
      const methaneRecovery = new InputSummaryItem();
      const cakeDryness = new InputSummaryItem();
      const recovery = new InputSummaryItem();
      const recycle = new InputSummaryItem();

      switch (unit) {
        case PlantUnitType.CHP:
          summary.subSection[PlantUnitType.CHP] = new InputSummary();
          // 38%
          if (overwriteValues || plantSpec.electricalEfficiency.value == null) {
            plantSpec.electricalEfficiency.value = 38;
            plantSpec.electricalEfficiency.unit = '000000000000000000000401';
          }
          electricalEfficiency.name = PlantSpecificationsPropName.electricalEfficiency;
          electricalEfficiency.unit = plantSpec.electricalEfficiency.unit;
          electricalEfficiency.value = String(plantSpec.electricalEfficiency.value);
          electricalEfficiency.path = ['plantSpecifications', 'electricalEfficiency', 'value'];
          summary.subSection[PlantUnitType.CHP].inputs.push(electricalEfficiency);
          // 43%
          if (overwriteValues || plantSpec.thermalEfficiency.value == null) {
            plantSpec.thermalEfficiency.value = 43;
            plantSpec.thermalEfficiency.unit = '000000000000000000000401';
          }
          thermalEfficiency.name = PlantSpecificationsPropName.thermalEfficiency;
          thermalEfficiency.unit = plantSpec.thermalEfficiency.unit;
          thermalEfficiency.value = String(plantSpec.thermalEfficiency.value);
          thermalEfficiency.path = ['plantSpecifications', 'thermalEfficiency', 'value'];
          summary.subSection[PlantUnitType.CHP].inputs.push(thermalEfficiency);
          break;
        case PlantUnitType.BOILER:
          summary.subSection[PlantUnitType.BOILER] = new InputSummary();
          // 75%
          if (overwriteValues || plantSpec.thermalEfficiency.value == null) {
            plantSpec.thermalEfficiency.value = 75;
            plantSpec.thermalEfficiency.unit = '000000000000000000000401';
          }
          thermalEfficiency.name = PlantSpecificationsPropName.thermalEfficiency;
          thermalEfficiency.unit = plantSpec.thermalEfficiency.unit;
          thermalEfficiency.value = String(plantSpec.thermalEfficiency.value);
          thermalEfficiency.path = ['plantSpecifications', 'thermalEfficiency', 'value'];
          summary.subSection[PlantUnitType.BOILER].inputs.push(thermalEfficiency);
          break;
        case PlantUnitType.GENERATOR:
          summary.subSection[PlantUnitType.GENERATOR] = new InputSummary();
          // 38%
          if (overwriteValues || plantSpec.electricalEfficiency.value == null) {
            plantSpec.electricalEfficiency.value = 38;
            plantSpec.electricalEfficiency.unit = '000000000000000000000401';
          }
          electricalEfficiency.name = PlantSpecificationsPropName.electricalEfficiency;
          electricalEfficiency.unit = plantSpec.electricalEfficiency.unit;
          electricalEfficiency.value = String(plantSpec.electricalEfficiency.value);
          electricalEfficiency.path = ['plantSpecifications', 'electricalEfficiency', 'value'];
          summary.subSection[PlantUnitType.GENERATOR].inputs.push(electricalEfficiency);
          break;
        case PlantUnitType.UPGRADER:
          summary.subSection[PlantUnitType.UPGRADER] = new InputSummary();
          // 98%
          if (overwriteValues || plantSpec.methaneRecovery.value == null) {
            plantSpec.methaneRecovery.value = 98;
            plantSpec.methaneRecovery.unit = '000000000000000000000401';
          }
          methaneRecovery.name = PlantSpecificationsPropName.methaneRecovery;
          methaneRecovery.unit = plantSpec.methaneRecovery.unit;
          methaneRecovery.value = String(plantSpec.methaneRecovery.value);
          methaneRecovery.path = ['plantSpecifications', 'methaneRecovery', 'value'];
          summary.subSection[PlantUnitType.UPGRADER].inputs.push(methaneRecovery);
          break;
        case PlantUnitType.DEWATERING:
          summary.subSection[PlantUnitType.DEWATERING] = new InputSummary();
          switch (plantSpec.dewateringType) {
            case 'PROJECT.PLANT_SPEC.CENTRIFUGE':
              // 30%
              if (overwriteValues || plantSpec.cakeDryness.value == null) {
                plantSpec.cakeDryness.value = 30;
                plantSpec.cakeDryness.unit = '000000000000000000000401';
              }
              cakeDryness.name = PlantSpecificationsPropName.cakeDryness;
              cakeDryness.unit = plantSpec.cakeDryness.unit;
              cakeDryness.value = String(plantSpec.cakeDryness.value);
              cakeDryness.path = ['plantSpecifications', 'cakeDryness', 'value'];
              summary.subSection[PlantUnitType.DEWATERING].inputs.push(cakeDryness);
              // 55%
              if (overwriteValues || plantSpec.recovery.value == null) {
                plantSpec.recovery.value = 55;
                plantSpec.recovery.unit = '000000000000000000000401';
              }
              recovery.name = PlantSpecificationsPropName.recovery;
              recovery.unit = plantSpec.recovery.unit;
              recovery.value = String(plantSpec.recovery.value);
              recovery.path = ['plantSpecifications', 'recovery', 'value'];
              summary.subSection[PlantUnitType.DEWATERING].inputs.push(recovery);
              // 50%
              if (overwriteValues || plantSpec.recycle.value == null) {
                plantSpec.recycle.value = 50;
                plantSpec.recycle.unit = '000000000000000000000401';
              }
              recycle.name = PlantSpecificationsPropName.recycle;
              recycle.unit = plantSpec.recycle.unit;
              recycle.value = String(plantSpec.recycle.value);
              recycle.path = ['plantSpecifications', 'recycle', 'value'];
              summary.subSection[PlantUnitType.DEWATERING].inputs.push(recycle);
              break;
            case 'PROJECT.PLANT_SPEC.SCREW_PRESS':
              // 40%
              if (overwriteValues || plantSpec.cakeDryness.value == null) {
                plantSpec.cakeDryness.value = 40;
                plantSpec.cakeDryness.unit = '000000000000000000000401';
              }
              cakeDryness.name = PlantSpecificationsPropName.cakeDryness;
              cakeDryness.unit = plantSpec.cakeDryness.unit;
              cakeDryness.value = String(plantSpec.cakeDryness.value);
              cakeDryness.path = ['plantSpecifications', 'cakeDryness', 'value'];
              summary.subSection[PlantUnitType.DEWATERING].inputs.push(cakeDryness);
              // 40%
              if (overwriteValues || plantSpec.recovery.value == null) {
                plantSpec.recovery.value = 40;
                plantSpec.recovery.unit = '000000000000000000000401';
              }
              recovery.name = PlantSpecificationsPropName.recovery;
              recovery.unit = plantSpec.recovery.unit;
              recovery.value = String(plantSpec.recovery.value);
              recovery.path = ['plantSpecifications', 'recovery', 'value'];
              summary.subSection[PlantUnitType.DEWATERING].inputs.push(recovery);
              // 50%
              if (overwriteValues || plantSpec.recycle.value == null) {
                plantSpec.recycle.value = 50;
                plantSpec.recycle.unit = '000000000000000000000401';
              }
              recycle.name = PlantSpecificationsPropName.recycle;
              recycle.unit = plantSpec.recycle.unit;
              recycle.value = String(plantSpec.recycle.value);
              recycle.path = ['plantSpecifications', 'recycle', 'value'];
              summary.subSection[PlantUnitType.DEWATERING].inputs.push(recycle);
              break;
          }
          break;
      }
    }
  }

  /**
   * Sets the assumed values for the feedstock based on the BMP type
   * @param feedstock reference to the feedstock
   * @param index index of the feedstock in the project feedstock list
   * @param bmpType whether the feedstock uses BMP or biogas/biomethane yield
   * @param yieldType whether the yield is biogas or biomethane
   */
  setFeedstockAssumptions(feedstock: Feedstock, index: number, bmpType: string, yieldType?: string) {
    const summary = this.projectAssumptions[ValidationSectionName.Feedstock].subSection[feedstock.name] = new InputSummary();
    summary.subSection[FeedstockPropName.bmpPotential] = new InputSummary();

    const ch4Concentration = new InputSummaryItem();
    const period = new InputSummaryItem();

    switch (bmpType) {
      case FeedstockBmpType.BMP_DIRECT:
        feedstock.bmpPotential.type = FeedstockBmpType.BMP_DIRECT;
        // 60%
        if (feedstock.bmpPotential.bmpDirect.ch4Concentration.value == null) {
          feedstock.bmpPotential.bmpDirect.ch4Concentration.value = 60;
          feedstock.bmpPotential.bmpDirect.ch4Concentration.unit = '000000000000000000000401';
        }
        ch4Concentration.name = FeedstockBmpPotentialDirectPropName.ch4Concentration;
        ch4Concentration.unit = feedstock.bmpPotential.bmpDirect.ch4Concentration.unit;
        ch4Concentration.value = String(feedstock.bmpPotential.bmpDirect.ch4Concentration.value);
        ch4Concentration.path = ['feedstocks', String(index), 'bmpPotential', 'bmpDirect', 'ch4Concentration', 'value'];
        summary.subSection[FeedstockPropName.bmpPotential].inputs.push(ch4Concentration);
        // 30 days
        if (feedstock.bmpPotential.bmpDirect.period.value == null) {
          feedstock.bmpPotential.bmpDirect.period.value = 30;
          feedstock.bmpPotential.bmpDirect.period.unit = '000000000000000000001302';
        }
        period.name = FeedstockBmpPotentialDirectPropName.period;
        period.unit = feedstock.bmpPotential.bmpDirect.period.unit;
        period.value = String(feedstock.bmpPotential.bmpDirect.period.value);
        period.path = ['feedstocks', String(index), 'bmpPotential', 'bmpDirect', 'period', 'value'];
        summary.subSection[FeedstockPropName.bmpPotential].inputs.push(period);
        break;
      case FeedstockBmpType.BIOGAS_YIELD:
        feedstock.bmpPotential.type = FeedstockBmpType.BIOGAS_YIELD;
        // 60%
        if (feedstock.bmpPotential.bmpYield.ch4Concentration.value == null) {
          feedstock.bmpPotential.bmpYield.ch4Concentration.value = 60;
          feedstock.bmpPotential.bmpYield.ch4Concentration.unit = '000000000000000000000401';
        }
        ch4Concentration.name = FeedstockBmpPotentialYieldPropName.ch4Concentration;
        ch4Concentration.unit = feedstock.bmpPotential.bmpYield.ch4Concentration.unit;
        ch4Concentration.value = String(feedstock.bmpPotential.bmpYield.ch4Concentration.value);
        ch4Concentration.path = ['feedstocks', String(index), 'bmpPotential', 'bmpYield', 'ch4Concentration', 'value'];
        summary.subSection[FeedstockPropName.bmpPotential].inputs.push(ch4Concentration);
        // 30 days
        if (feedstock.bmpPotential.bmpYield.period.value == null) {
          feedstock.bmpPotential.bmpYield.period.value = 30;
          feedstock.bmpPotential.bmpYield.period.unit = '000000000000000000001302';
        }
        period.name = FeedstockBmpPotentialYieldPropName.period;
        period.unit = feedstock.bmpPotential.bmpYield.period.unit;
        period.value = String(feedstock.bmpPotential.bmpYield.period.value);
        period.path = ['feedstocks', String(index), 'bmpPotential', 'bmpYield', 'period', 'value'];
        summary.subSection[FeedstockPropName.bmpPotential].inputs.push(period);
        if (yieldType) {
          switch (yieldType) {
            case BmpYieldType.BIOGAS_YIELD:
              feedstock.bmpPotential.bmpYield.yieldType = BmpYieldType.BIOGAS_YIELD;
              break;
            case BmpYieldType.BIOMETH_YIELD:
              feedstock.bmpPotential.bmpYield.yieldType = BmpYieldType.BIOMETH_YIELD;
              break;
          }
        }
        break;
      default:
        feedstock.bmpPotential.type = null;
        break;
    }
  }

  /**
   * Sets the assumed values for the investment financials
   * @param investment reference to the investment data
   */
  setInvestmentAssumptions(investment: FinancialInvestment) {
    const summary = this.projectAssumptions[ValidationSectionName.Financial].subSection[FinancialPropName.investment] = new InputSummary();

    // 6%
    if (investment.discountRate.value == null || investment.discountRate.value === 0) {
      investment.discountRate.value = 6;
      investment.discountRate.unit = '000000000000000000000401';
    }
    const discountRate = new InputSummaryItem();
    discountRate.name = FinancialInvestmentPropName.discountRate;
    discountRate.unit = investment.discountRate.unit;
    discountRate.value = String(investment.discountRate.value);
    discountRate.path = ['financial', 'investment', 'discountRate', 'value'];
    summary.inputs.push(discountRate);
    // 0%
    if (investment.incomeTax.value == null || investment.incomeTax.value === 0) {
      investment.incomeTax.value = 0;
      investment.incomeTax.unit = '000000000000000000000401';
    }
    const incomeTax = new InputSummaryItem();
    incomeTax.name = FinancialInvestmentPropName.incomeTax;
    incomeTax.unit = investment.incomeTax.unit;
    incomeTax.value = String(investment.incomeTax.value);
    incomeTax.path = ['financial', 'investment', 'incomeTax', 'value'];
    summary.inputs.push(incomeTax);
    // 0%
    if (investment.distributions.value == null || investment.distributions.value === 0) {
      investment.distributions.value = 0;
      investment.distributions.unit = '000000000000000000005501';
    }
    const distributions = new InputSummaryItem();
    distributions.name = FinancialInvestmentPropName.distributions;
    distributions.unit = investment.distributions.unit;
    distributions.value = String(investment.distributions.value);
    distributions.path = ['financial', 'investment', 'distributions', 'value'];
    summary.inputs.push(distributions);
    // 100% of capex
    if (investment.residualValue.value == null || investment.residualValue.value === 0) {
      investment.residualValue.value = 100;
      investment.residualValue.unit = '000000000000000000005401';
    }
    const residualValue = new InputSummaryItem();
    residualValue.name = FinancialInvestmentPropName.residualValue;
    residualValue.unit = investment.residualValue.unit;
    residualValue.value = String(investment.residualValue.value);
    residualValue.path = ['financial', 'investment', 'residualValue', 'value'];
    summary.inputs.push(residualValue);
  }
}
